import * as React from 'react';
import { Avatar, Box, Card, CardContent, Typography, Link, Stack, Rating } from '@mui/material';
import { useSiteContent } from '../context/SiteContentContext';
import { COLORS } from '../theme/colors';
import patternImage from '../images/bg.png'
import { Divider } from '@mui/material';
const GOOGLE_REVIEWS_URL =
    'https://www.google.com/maps/place/Alpha+Tools+%26+Components/@44.3260951,-79.6812453,17z/data=!3m1!4b1!4m6!3m5!1s0x882abdd65ea48c8f:0x2221bd3d74802df5!8m2!3d44.3260951!4d-79.6786704!16s%2Fg%2F11f6xz9qkp?entry=ttu&g_ep=EgoyMDI1MDMyNS4xIKXMDSoASAFQAw%3D%3D';

// Same "colored circle + initial" avatar style Google's own review widget uses
// when a reviewer has no profile photo.
function stringToColor(string) {
    let hash = 0;
    for (let i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
}

function authorAvatarProps(name, avatarUrl) {
    if (avatarUrl) {
        return { src: avatarUrl, alt: name, sx: { width: 40, height: 40 } };
    }
    const initial = name.trim().charAt(0).toUpperCase();
    return {
        sx: { bgcolor: stringToColor(name), width: 40, height: 40, fontSize: '1rem' },
        children: initial,
    };
}

function Testimonials({ testimonials: testimonialsProp }) {
    const { testimonials: contextTestimonials } = useSiteContent();
    const testimonials = testimonialsProp ?? contextTestimonials;
    const withQuote = testimonials.filter((t) => t.quote);
    const withoutQuote = testimonials.filter((t) => !t.quote);

    return (
        <Box
        
            sx={{
                boxShadow: ' rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px;',
                display: 'flex',
                position: 'relative',
                padding: { xs: 3, sm: 5, md: 10 },
                justifyContent: 'center',
                alignItems: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundImage:
                    'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)',
                backgroundColor: COLORS.darkSection,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${patternImage})`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    opacity: 0.05,
                    zIndex: 1,
                },
            }}>
                 <Divider sx={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: '80%', bgcolor: '#fff' }} />
            <Stack spacing={3} alignItems="center" sx={{ width: '100%', maxWidth: 800 }}>
                <Typography variant="h4" sx={{ color: '#fff' }}>
                    What Our Customers Say
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Rating value={5} readOnly size="small" sx={{ color: COLORS.star }} />
                    <Typography sx={{ color: '#fff' }}>5.0 from {testimonials.length} Google reviews</Typography>
                </Stack>

                {withQuote.map((testimonial) => (
                    <Card
                        key={testimonial.id}
                        sx={{ width: '100%', boxShadow: 3, borderRadius: 2, bgcolor: COLORS.overlay }}
                    >
                        <CardContent>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Avatar {...authorAvatarProps(testimonial.author, testimonial.avatarUrl)} />
                                <Box>
                                    <Typography sx={{ color: 'white', fontWeight: 600 }}>
                                        {testimonial.author}
                                    </Typography>
                                    <Rating
                                        value={testimonial.stars}
                                        readOnly
                                        size="small"
                                        sx={{ color: COLORS.star }}
                                    />
                                </Box>
                            </Stack>
                            <Typography sx={{ color: 'white', mt: 2, fontStyle: 'italic' }}>
                                "{testimonial.quote}"
                            </Typography>
                            <Typography sx={{ color: 'grey.400', mt: 2 }}>
                                Google review
                            </Typography>
                        </CardContent>
                    </Card>
                ))}

                {withoutQuote.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            gap: 2,
                            width: '100%',
                        }}
                    >
                        {withoutQuote.map((testimonial) => (
                            <Card
                                key={testimonial.id}
                                sx={{
                                    width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.33% - 11px)' },
                                    boxShadow: 3,
                                    borderRadius: 2,
                                    bgcolor: COLORS.overlay,
                                }}
                            >
                                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Avatar {...authorAvatarProps(testimonial.author, testimonial.avatarUrl)} />
                                        <Box>
                                            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                                                {testimonial.author}
                                            </Typography>
                                            <Rating
                                                value={testimonial.stars}
                                                readOnly
                                                size="small"
                                                sx={{ color: COLORS.star }}
                                            />
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                )}

                <Link
                    href={GOOGLE_REVIEWS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: COLORS.link }}
                >
                    See all reviews on Google
                </Link>
            </Stack>
        </Box>
    );
}

export default Testimonials;
